/* import __COLOCATED_TEMPLATE__ from './editor.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */

import type AppService from 'embercom/services/app-service';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { BlocksDocument } from '@intercom/composer-ember';
import type ArticleContent from 'embercom/models/articles/article-content';
import { MAX_SUMMARY_SIZE, MAX_PREVIEW_SIZE } from 'embercom/models/article';
import ArticlesComposerConfig, {
  type DynamicContentItem,
} from 'embercom/lib/articles/composer-config';
import { htmlToTextContent } from 'embercom/lib/html-unescape';
import type ClipboardService from 'embercom/services/clipboard-service';
import type KnowledgeHubEditorService from 'embercom/services/knowledge-hub-editor-service';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type { Block } from '@intercom/interblocks.ts';
import { captureException } from 'embercom/lib/sentry';

interface Args {
  activeContent: ArticleContent;
  defaultThemeColor: string;
  isContentReadinessContext?: boolean;
}

export default class Editor extends Component<Args> {
  @service declare appService: AppService;
  @service declare intercomEventService: any;
  @service declare helpCenterService: any;
  @service declare clipboardService: ClipboardService;
  @service declare knowledgeHubEditorService: KnowledgeHubEditorService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: $TSFixMe;
  @service declare intl: $TSFixMe;

  @tracked activeContentBlocksDocument!: BlocksDocument;
  @tracked prosemirrorComposer: $TSFixMe;
  maxSummarySize = MAX_SUMMARY_SIZE;
  clipboard: ClipboardJS | undefined;

  constructor(owner: any, args: Args) {
    super(owner, args);
    this.activeContentBlocksDocument = new BlocksDocument(this.activeContentBlocks);
  }

  get dynamicContentItems(): DynamicContentItem[] {
    return this.knowledgeHubService.dynamicContentItems;
  }

  get app() {
    return this.appService.app;
  }

  get activeContent() {
    return this.args.activeContent;
  }

  get prosemirrorComposerConfig() {
    let eventService = this.intercomEventService;
    let analytics = {
      trackAnalyticsEvent: (eventName: any, attrs: any) => {
        eventService.trackAnalyticsEvent({
          action: eventName,
          object: 'article',
          place: 'composer',
          ...attrs,
        });
      },
    };

    let anchorLinkOnClick = (heading: any) => {
      this.copyHeadingLinkToClipboard(heading);
    };

    let config = new ArticlesComposerConfig(
      this.app,
      this.activeContent.textDirection,
      analytics,
      anchorLinkOnClick,
      true,
      true,
      this.dynamicContentItems,
      this.activeContent.locale,
    );

    config.upload.onUploadStart = this.knowledgeHubEditorService.uploadFileStarted;
    config.upload.onUploadFinish = this.knowledgeHubEditorService.uploadFileFinished;
    config.enableDiffWidget =
      !!this.args.isContentReadinessContext && this.appService.app.canUseContentReadinessScoringUI;

    return config;
  }

  get activeContentBlocks(): Block[] {
    if (this.activeContent) {
      if (
        this.appService.app.canUseTeamKfDynamicContentForArticles &&
        this.activeContent.jsonBlocksForEditing
      ) {
        return this.activeContent.jsonBlocksForEditing;
      }
      if (this.activeContent.jsonBlocks) {
        return this.activeContent.jsonBlocks;
      }
    }
    return [];
  }

  private fileInputChangeListener = (event: Event) => {
    let target = event.target;
    if (target instanceof HTMLInputElement && target.type === 'file' && target.files?.length) {
      this.knowledgeHubEditorService.uploadFileStarted();
    }
  };

  willDestroy() {
    super.willDestroy();
    this.knowledgeHubEditorService.composerInstance = null;
    this.knowledgeHubEditorService.uploadFileFinished();
    document.removeEventListener('change', this.fileInputChangeListener, true);
  }

  @action
  prosemirrorComposerReady(composer: any) {
    this.prosemirrorComposer = composer;
    this.knowledgeHubEditorService.composerInstance = composer;
    document.addEventListener('change', this.fileInputChangeListener, true);
  }

  @action
  prosemirrorComposerChanged(newBlocksDocument: BlocksDocument) {
    this.activeContent.set('jsonBlocks', newBlocksDocument.blocks);
  }

  copyHeadingLinkToClipboard(heading: any) {
    let url = `${this.activeContent.publicUrl}#${heading.idAttribute}`;
    let onSuccess = () => {
      this._trackCopyLink(heading, true);
      let previewText = htmlToTextContent(heading.text);
      previewText =
        previewText.length > MAX_PREVIEW_SIZE
          ? `${previewText.substring(0, MAX_PREVIEW_SIZE)}…`
          : previewText;
      this.notificationsService.notifyConfirmation(
        this.intl.t('articles.editor.copied-anchor-link-to-place', { preview_text: previewText }),
      );
      this.clipboard?.destroy();
    };
    let onError = () => {
      this._trackCopyLink(heading, false);
      this.notificationsService.notifyWarning(
        this.intl.t('knowledge-hub.content-editor.article-content.clip-board-error'),
      );
      this.clipboard?.destroy();
    };
    this.clipboard = this.clipboardService.createClipboard(
      '.js__prosemirror-composer-copy-clipboard-element',
      url,
      onSuccess,
      onError,
    );
  }

  @action
  handleComposerError(error: unknown) {
    captureException(error instanceof Error ? error : new Error(String(error)), {
      fingerprint: ['article-composer', 'prosemirror-error'],
      extra: {
        article_id: this.activeContent?.id,
        locale: this.activeContent?.locale,
        state: this.activeContent?.state,
      },
      tags: {
        responsibleTeam: 'team-knowledge-foundations',
        responsible_team: 'team-knowledge-foundations',
        component: 'article-composer',
      },
    });
  }

  _trackCopyLink(heading: any, success: any) {
    this.intercomEventService.trackAnalyticsEvent({
      object: 'anchor_link_button',
      action: 'clicked',
      locale: this.currentlySelectedLocaleId,
      anchor_link_type: heading.type,
      success,
    });
  }

  get currentlySelectedLocaleId() {
    if (!this.activeContent) {
      return '';
    }
    return this.activeContent.locale;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::ArticleContent::Edit::Editor': typeof Editor;
    'knowledge-hub/content-editor/article-content/edit/editor': typeof Editor;
  }
}
